<template>
  <div :class="`appointments-page mode-${$mode}`" data-testid="appointments-page-v1">
    <v-container fluid>
      <div class="py-4 d-flex flex-row justify-center align-center align-content-center">
        <div class="d-flex align-center font-weight-bold pr-2 text-no-wrap mr-2">Filter by:</div>
        <v-row class="d-flex flex-row align-center align-content-center">
          <v-col :cols="selectedWarehouse.geolocation ? 2 : 3">
            <warehouse-select
              :restricted-warehouse-ids="$me.warehouseAccessList"
              should-auto-select-if-single-item
              class="pt-2"
              hide-icon
              ref="warehouse-select"
              v-model="selectedWarehouse"
              custom-persistent-label="Warehouse"
              show-store-number></warehouse-select>
          </v-col>
          <v-col :cols="3">
            <dock-select
              ref="dock-select"
              data-testid="appointments-page-docks-select"
              class="pt-2"
              hide-icon
              prepend-icon="mdi-truck-delivery-outline"
              filter-mode
              v-model="selectedDocks"
              :docks="warehouseDocks"
              :selected-warehouse="selectedWarehouse"
              :disabled="!selectedWarehouse.id"
              custom-persistent-label="Docks">
            </dock-select>
          </v-col>
          <v-col :cols="3">
            <load-type-select
              ref="loadtype-select"
              :disabled="!selectedWarehouse || !warehouseDocks?.length"
              :selected-warehouse="selectedWarehouse"
              :selected-docks="warehouseDocks"
              :filter-mode="true"
              class="pt-2"
              hide-icon
              :include-durations="false"
              :multi-select="true"
              :return-object="false"
              :select-all="true"
              v-model="selectedLoadtypes"
              custom-persistent-label="Load Types">
            </load-type-select>
          </v-col>
          <v-col :cols="3">
            <status-select
              ref="status-select"
              class="pt-2"
              hide-icon
              v-model="selectedStatuses"
              custom-persistent-label="Status">
            </status-select>
          </v-col>
          <v-col :cols="1" v-if="selectedWarehouse.geolocation" class="pr-0">
            <check-in-notification-dropdown
              :appointments="warehouseEventsByDock"
              :warehouse="selectedWarehouse">
            </check-in-notification-dropdown>
          </v-col>
        </v-row>
      </div>
    </v-container>

    <calendar-header></calendar-header>
    <div
      :class="`calendar-wrapper is-relative ${useLightGridTheme ? 'light-grid-theme' : ''}`"
      v-if="$mode === 'grid'">
      <appointments-loader data-testid="grid-loader" :is-loading="isLoading"></appointments-loader>
      <!-- Dynamic calendar component rendering based on selected calendar view -->
      <component
        v-if="mounted"
        :are-events-ready="mounted"
        :is="calendarComponent"
        :docks="selectedDocks"
        @is-dock-capacity-toggle="setIsDockCapacityToggle"
        @set-loader="val => (isLoading = val)"
        :events="warehouseEventsByDock"></component>
      <v-row class="px-6">
        <v-col>
          <v-switch
            class="mt-0"
            v-model="useLightGridTheme"
            color="secondary"
            label="Light Grid Theme"></v-switch>
        </v-col>
        <v-col>
          <calendar-legend class="text-right"></calendar-legend>
        </v-col>
      </v-row>
      <no-warehouse-selection-overlay
        :is-visible="shouldShowNoSelectionOverlay"></no-warehouse-selection-overlay>
    </div>
    <div class="is-relative" v-else>
      <appointments-list
        :is-loading="isLoading"
        :appointments="filterWithinSelectedRange(warehouseEventsByDock)"
        :filter-locally="true"></appointments-list>
      <no-warehouse-selection-overlay
        :is-visible="shouldShowNoSelectionOverlay"></no-warehouse-selection-overlay>
    </div>
  </div>
</template>

<script>
import calendarMixin from '@/modules/calendar/mixins/calendarMixin';
import localStorageMixin from '@satellite/components/mixins/localStorageMixin';
import appointmentsPageMixin from '@/components/mixins/appointmentsPageMixin';
import moment from 'moment-timezone';

/**
 * Appointments page
 * @displayName Appointments Page V1
 */
export default {
  mixins: [calendarMixin, localStorageMixin, appointmentsPageMixin],
  computed: {},
  data() {
    return {
      autoSelectStatuses: true,
      updatedDockScheduleCount: 0,
      isLoading: false,
      isDockCapacityToggle: false
    };
  },
  methods: {
    setIsDockCapacityToggle(isDockCapacityToggle) {
      this.isDockCapacityToggle = isDockCapacityToggle;
    },
    filterWithinSelectedRange(appts) {
      // Filter locally, since the full appointments list may have
      // appointments outside of the range. The calendar grid is already handling that,
      // but list view isn't.
      const unit = this.$viewType.toLowerCase();
      const warehouseTimezone = this.selectedWarehouse?.timezone;

      const momentBase = warehouseTimezone
        ? moment(this.$selectedDate).tz(warehouseTimezone, true).startOf(unit)
        : moment(this.$selectedDate).startOf(unit);

      const startDate = momentBase.clone().toISOString();
      const endDate = momentBase.clone().add(1, `${unit}s`).toISOString();

      return appts.filter(appt => {
        const apptStartMoment = moment(appt.start).tz(warehouseTimezone, true);
        return apptStartMoment.isSameOrAfter(startDate) && apptStartMoment.isSameOrBefore(endDate);
      });
    }
  },
  beforeMount() {
    this.setStoredCalendarData();
  },
  beforeDestroy() {
    this.$eventHub.$off('update-Dock');
    ['update', 'create', 'delete'].forEach(operation =>
      this.$eventHub.$off(`${operation}-Appointment`)
    );
  }
};
</script>
